import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { CurrentUrlService } from '@cumlaude/shared-services';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { getUrl } from '@cumlaude/shared-components-menu';
import { HoofdnavigatieService } from '../../services/hoofdnavigatie.service';
import { NavigatieUitklapService } from '../../services/navigatie-uitklap.service';
import { HeaderActiesService } from '../../services/header-acties.service';
import { NavigatieRijComponent } from './navigatie-rij/navigatie-rij.component';

/**
 * Figma (node 7006:54012, instance "Favorieten" op x=274/y=73 t.o.v. de pagina, terwijl de content
 * op x=256 begint en app-paginakop 68px hoog is): de popout raakt de rail en de paginakop niet
 * volledig -- een klein gaatje van 18px links en 5px boven de rand onder de paginakop. Ankerpunt is
 * de hele rail (die op dezelfde y-positie als de paginakop begint), met vaste offsets zodat de
 * popout niet aan de rail-interne layout (logo/divider/knop-posities) hangt.
 */
const FAVORIETEN_POSITIONS: ConnectedPosition[] = [{ originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 18, offsetY: 73 }];

/**
 * De verticale hoofdnavigatie: logo, Startpagina, Favorieten, de Dashboards-sectie met de
 * uitklapbare boom, en onderin de in-/uitklapknop van de rail.
 */
@Component({
	selector: 'app-hoofdnavigatie',
	templateUrl: './hoofdnavigatie.component.html',
	styleUrls: ['./hoofdnavigatie.component.scss'],
	imports: [RouterLink, IconComponent, TooltipDirective, NavigatieRijComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoofdnavigatieComponent {
	protected readonly nav = inject(HoofdnavigatieService);
	protected readonly uitklap = inject(NavigatieUitklapService);
	private readonly currentUrl = inject(CurrentUrlService);
	private readonly headerActies = inject(HeaderActiesService);

	/** Ankerpunt voor de Favorieten-popout: de hele rail, niet de knop -- zie FAVORIETEN_POSITIONS. */
	private readonly elementRef = inject(ElementRef);

	protected readonly ingeklapt = this.uitklap.ingeklapt;

	protected readonly startUrl = computed(() => {
		const start = this.nav.startRoute();
		return start ? getUrl({ routeElements: start.elements! }) : '/start';
	});

	protected readonly startTitel = computed(() =>
		this.nav.sectie() === 'beheer' ? 'Terug naar CumLaude' : (this.nav.startRoute()?.routeData?.name ?? 'Startpagina'),
	);

	protected readonly startActief = computed(() => this.currentUrl.currentPath() === this.startUrl());

	protected readonly rijen = computed(() => {
		const rijen = this.nav.zichtbareRijen();
		return this.ingeklapt() ? rijen.filter((rij) => rij.niveau === 0) : rijen;
	});

	protected readonly favorietenOpen = signal(false);

	openFavorieten() {
		this.favorietenOpen.set(true);
		const dialogRef = this.headerActies.openFavorieten(this.elementRef, FAVORIETEN_POSITIONS, 'cdk-overlay-transparent-backdrop');
		dialogRef.closed.subscribe(() => this.favorietenOpen.set(false));
	}
}
